import React, { useState, useRef, useEffect } from "react";
import { Globe, Loader2, ChevronDown, Check, Film } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslate } from "../context/TranslateContext";
import { LANGUAGES } from "../mock/data";

const ORDERED = [
  ...LANGUAGES.filter((l) => l.code === "en"),
  ...LANGUAGES.filter((l) => l.indian),
];

// A handful of languages shown inline; the rest live behind "More".
const FEATURED = ["en", "hi", "ml", "ta", "te", "kn", "bn", "mr"];
const featured = ORDERED.filter((l) => FEATURED.includes(l.code));
const more = ORDERED.filter((l) => !FEATURED.includes(l.code));

const chip = (active, rtl) =>
  `shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 font-body transition-colors duration-200 ${rtl ? "text-sm" : "text-xs"} ${
    active ? "border-gold bg-gold text-ink" : "border-edge text-ash hover:border-gold hover:text-gold"
  }`;

// Thin, single-line language selector. Popular Indian languages inline + a "More" dropdown.
export const LanguageBar = () => {
  const { lang, changeLanguage, loading } = useTranslate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, []);

  const activeInMore = more.find((l) => l.code === lang.code);

  const pick = (l) => { changeLanguage(l); setOpen(false); };

  return (
    <div data-testid="language-bar" className="border-t border-edge bg-surface/50 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1400px] items-center gap-2 px-6 py-1.5 md:px-12">
        <span className="flex shrink-0 items-center gap-1.5 font-body text-xs font-medium uppercase tracking-widest text-gold">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          <span className="hidden sm:inline">{lang.code === "en" ? "Translate" : "Translated"}</span>
        </span>

        <div className="flex flex-1 items-center gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {featured.map((l) => {
            const active = lang.code === l.code;
            return (
              <button key={l.code} data-testid={`langbar-option-${l.code}`} onClick={() => pick(l)} title={l.name}
                className={chip(active, l.rtl)}>
                {l.native}
                {l.code === "en" ? "" : <span className="ml-1 text-xs opacity-60">{l.name}</span>}
              </button>
            );
          })}

          {/* Network edition banner — links to the MalayalaCinema edition */}
          <Link to="/malayalam" data-testid="langbar-malayala-cinema" title="Visit MalayalaCinema.com"
            className="ml-1 inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-gold bg-gold/15 px-3 py-1 font-body text-xs font-semibold text-gold transition-colors duration-200 hover:bg-gold hover:text-ink">
            <Film className="h-3.5 w-3.5" /> Malayala Cinema <span aria-hidden>→</span>
          </Link>
        </div>

        {/* More dropdown */}
        <div className="relative shrink-0" ref={ref}>
          <button data-testid="langbar-more" onClick={() => setOpen((o) => !o)}
            className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1 font-body text-xs transition-colors duration-200 ${
              activeInMore ? "border-gold bg-gold text-ink" : "border-edge text-ash hover:border-gold hover:text-gold"
            }`}>
            {activeInMore ? activeInMore.native : "More"}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>

          <AnimatePresence>
            {open && (
              <motion.div data-testid="langbar-more-panel"
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="absolute right-0 top-full z-50 mt-2 max-h-[60vh] w-[min(88vw,26rem)] overflow-y-auto rounded-2xl border border-edge bg-paper p-3 shadow-[0_16px_50px_-12px_rgba(0,0,0,0.45)]">
                <div className="mb-2 px-1 font-body text-[0.65rem] uppercase tracking-[0.2em] text-ash">{"More Indian languages"}</div>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {more.map((l) => {
                    const active = lang.code === l.code;
                    return (
                      <button key={l.code} data-testid={`langbar-option-${l.code}`} onClick={() => pick(l)} title={l.name}
                        className={`flex items-center justify-between gap-1 rounded-xl border px-2.5 py-1.5 text-left font-body transition-colors duration-200 ${l.rtl ? "text-sm" : "text-xs"} ${
                          active ? "border-gold bg-gold/10 text-gold" : "border-edge text-ash hover:border-gold hover:text-gold"
                        }`}>
                        <span className="truncate">
                          {l.native}<span className="ml-1 opacity-60">{l.name}</span>
                        </span>
                        {active && <Check className="h-3.5 w-3.5 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
